import React, { type ReactNode } from "react";
import Textarea, { type TextareaErrorProps } from "../../Controls/Textarea/Textarea";
import MappingField, {
  type MappingFieldColumns,
  type MappingFieldRowData,
  type MappingFieldValue,
} from "./MappingField/MappingField";
import SearchQueryField from "./SearchQueryField/SearchQueryField";
import DynamicMappingField from "./DynamicMappingField/DynamicMappingField";
import "./GenericField.scss";
import {
  CheckBox,
  RadioButtonsList,
  TagsSelect,
  TextInput,
  SelectInput,
  type SelectOption,
  DateInput,
  TextEditor,
  type TextEditorToolbarItem,
  parseDateInputToIsoDateString,
} from "@SeemplicityDev/shared";

export const fieldTypeOptions = {
  TEXT: "STRING",
  LABEL: "LABEL",
  TAGS_LIST: "LIST",
  SELECT: "SELECT",
  MULTISELECT: "SELECT_MULTI",
  SELECT_QUERY: "SELECT_QUERY",
  DATE: "DATE",
  NUMBER: "NUMBER",
  TEXT_AREA: "TEXTAREA",
  HTML_TEXT_AREA: "HTML_TEXTAREA",
  MAPPING: "MAPPING",
  UNIQUE_MAPPING: "UNIQUE_MAPPING",
  DYNAMIC_MAPPING: "SELECT_MAPPING",
  RADIO: "RADIO",
  COLOR_SELECT: "COLOR_SELECT",
  AUTOCOMPLETE_ONLY_TEXTAREA: "AUTOCOMPLETE_ONLY_TEXTAREA",
  CHECKBOX: "CHECKBOX",
  BOOLEAN: "BOOL",
  INLINE_AUTOCOMPLETE: "INLINE_AUTOCOMPLETE",
  JSON: "JSON",
} as const;

export type FieldType = (typeof fieldTypeOptions)[keyof typeof fieldTypeOptions];

export const textEditorFields = [
  fieldTypeOptions.AUTOCOMPLETE_ONLY_TEXTAREA,
  fieldTypeOptions.INLINE_AUTOCOMPLETE,
  fieldTypeOptions.HTML_TEXT_AREA,
];

/** Raw option shape returned by the server for select/tags/radio/color fields. */
export interface ServerFieldOption {
  id?: string;
  name?: string;
  disabled?: boolean;
  // Lets a caller supply an explicit swatch color for a color field (e.g. a
  // themed CSS var) while `id` remains the submitted value. Falls back to `id`.
  color?: string;
}

/** Normalized option shape consumed by the design-system inputs. */
export interface FieldSelectOption {
  value?: string;
  label?: string;
  name?: string;
  disabled?: boolean;
  customPrefix?: ReactNode;
}

export const formatSelectOptionsFromServer = (
  options?: ServerFieldOption[]
): FieldSelectOption[] | undefined =>
  options?.map((option) => {
    return { value: option?.id, label: option?.name };
  });

export const formatColorSelectOptionsFromServer = (
  options?: ServerFieldOption[]
): FieldSelectOption[] | undefined =>
  options?.map((option) => {
    return {
      value: option?.id,
      label: option?.name,
      customPrefix: (
        <div
          className={"color-square"}
          style={{ backgroundColor: option?.color ?? option?.id }}
        />
      ),
    };
  });

export const formatRadioOptionsFromServer = (
  options?: ServerFieldOption[]
): FieldSelectOption[] | undefined =>
  options?.map((option) => {
    return {
      value: option?.id,
      label: option?.name,
      name: option?.name,
      disabled: option?.disabled,
    };
  });

export const getSelectOptionFromValue = (
  options: FieldSelectOption[] | undefined,
  value: unknown
): FieldSelectOption | null =>
  options?.find((option) => option.value === value) || null;

export const getMultiSelectOptionFromValue = (
  options: FieldSelectOption[] | undefined,
  value: string[] | undefined
): (FieldSelectOption | undefined)[] =>
  value?.map((id) => options?.find((option) => option.value === id)) || [];

export const getSelectValueForForm = (
  value: FieldSelectOption | null | undefined
): string | undefined => value?.value;

export const getMultiSelectValueForForm = (
  value: (FieldSelectOption | undefined)[] | undefined
): (string | undefined)[] | undefined =>
  value?.map((option) => option?.value);

interface MappingShape {
  columns?: unknown;
  rows?: { field_from?: unknown }[];
}

interface QueryShape {
  params?: unknown;
}

interface HtmlTextEditorValue {
  html: string;
  text: string;
}

export const DEFAULT_MAX_CHARACTERS = 50000;

type TextEditorRichFormatting = React.ComponentProps<
  typeof TextEditor
>["richFormatting"];

export interface GenericFieldProps {
  input?: string;
  label?: ReactNode;
  name?: string;
  value?: unknown;
  loading?: boolean;
  error?: unknown;
  clearable?: boolean;
  options?: ServerFieldOption[];
  onBlur?: () => void;
  onChange: (value: unknown) => void;
  inputValue?: string;
  hint?: ReactNode;
  onInputChange?: (value: string) => void;
  creatable?: boolean;
  query?: QueryShape;
  defaultValue?: HtmlTextEditorValue;
  setSingleOption?: boolean;
  setFirstAsDefault?: boolean;
  mapping?: MappingShape;
  placeholder?: ReactNode;
  classname?: string;
  disabled?: boolean;
  required?: boolean;
  description?: ReactNode;
  help?: ReactNode;
  hideLabelHelper?: boolean;
  direction?: string;
  max?: number;
  autoCompleteSettings?: unknown;
  viewMode?: boolean;
  size?: string;
  noMargin?: boolean;
  toolbarActions?: TextEditorToolbarItem[];
  richFormatting?: TextEditorRichFormatting;
  maxCharacters?: number;
  // Pass-through props spread into <GenericField> by callers (MappingFieldRow,
  // DynamicMappingFieldRow, TicketEndpointColumns/Keys). The original JS
  // component forwarded all rest props onward, so the contract is open-ended.
  onFocus?: () => void;
  control?: unknown;
  formState?: unknown;
  setValue?: unknown;
  onRowChange?: unknown;
  registerPrefix?: unknown;
  // Index signature mirrors the original JS contract of accepting arbitrary
  // forwarded props; the component spreads `props` onto MappingField /
  // DynamicMappingField / SearchQueryField.
  [key: string]: unknown;
}

const GenericField: React.FC<GenericFieldProps> = (props) => {
  const {
    input,
    label = "",
    name,
    value,
    loading,
    error = null,
    clearable = true,
    options,
    onBlur,
    onChange,
    inputValue,
    hint,
    onInputChange = () => {},
    creatable,
    query,
    defaultValue,
    setSingleOption = true,
    setFirstAsDefault = true,
    mapping,
    placeholder = "",
    classname = "",
    disabled = false,
    required,
    description = "",
    help = "",
    hideLabelHelper = true,
    direction = "",
    max,
    autoCompleteSettings,
    viewMode = false,
    toolbarActions,
    richFormatting,
    maxCharacters = DEFAULT_MAX_CHARACTERS,
  } = props;

  const getTextEditor = (isInline = false, showToolbar = true) => {
    return (
      <TextEditor
        maxCharacters={maxCharacters}
        wrapperClassName={`generic-field generic-field-text-area ${classname}`}
        data-testid={name}
        name={name}
        // GenericField types these as ReactNode for flexibility; TextEditor
        // narrows to string. Runtime values here are strings.
        hint={hint as string | undefined}
        placeholder={placeholder as string | undefined}
        noMargin
        // In manual-ticket, HTML textarea fields are stored as HTML string.
        // `TextEditor` expects an object, so adapt here.
        // If value was not yet set (i.e. not in the shape { html, text }), use defaultValue if it exists.
        // `value` is `unknown` here; runtime is either an HTML string (adapted
        // to { html, text }) or an existing { html, text } object.
        value={
          (typeof value === "string"
            ? { html: value, text: "" }
            : !value && defaultValue
              ? defaultValue
              : value) as { html?: string; text?: string } | null | undefined
        }
        disabled={disabled}
        onBlur={onBlur}
        size="max"
        labelTop
        onChange={(data: unknown) => {
          onChange(data);
        }}
        description={description as string | undefined}
        // `error` is `unknown` on GenericFieldProps; TextEditor narrows to ErrorProps.
        error={error as { message: string; type: string } | undefined}
        label={label as string | undefined}
        required={required}
        hideLabelHelper={hideLabelHelper}
        // `autoCompleteSettings` is `unknown`; forwarded verbatim to TextEditor.
        autoCompleteSettings={
          autoCompleteSettings as React.ComponentProps<
            typeof TextEditor
          >["autoCompleteSettings"]
        }
        isInline={isInline}
        hasToolbar={showToolbar}
        toolbarActions={toolbarActions}
        richFormatting={richFormatting}
      />
    );
  };

  switch (input) {
    case fieldTypeOptions.SELECT:
      const selectOptions = formatSelectOptionsFromServer(options);

      return (
        <SelectInput
          wrapperClassName={`generic-field generic-field-select ${classname}`}
          data-testid={name}
          noMargin
          viewMode={viewMode}
          inputValue={inputValue}
          name={name}
          // FieldSelectOption.label is `string | undefined`; SelectInput's
          // SelectValue requires `label: string`. Runtime labels are strings.
          value={getSelectOptionFromValue(selectOptions, value) as SelectOption | null}
          disabled={disabled}
          onBlur={onBlur}
          labelTop={!!label}
          setDefaultSingleOption={setSingleOption}
          onChange={(val: FieldSelectOption | null) =>
            onChange(getSelectValueForForm(val))
          }
          onInputChange={(val: string) => onInputChange(val)}
          // `error` is `unknown` on GenericFieldProps; SelectInput narrows to ErrorProps.
          error={error as { message?: string; type?: string } | undefined}
          isClearable={clearable}
          // `placeholder`/`label` are ReactNode for flexibility; SelectInput
          // narrows to string. Server-driven field labels can be JSX (shared-type gap).
          placeholder={placeholder as string | undefined}
          isSearchable
          label={label as string | undefined}
          isLoading={loading}
          options={selectOptions as SelectOption[] | undefined}
          required={required}
          hideLabelHelper={hideLabelHelper}
          description={description}
        />
      );
    case fieldTypeOptions.MULTISELECT:
      const multiSelectOptions = formatSelectOptionsFromServer(options);

      return (
        <SelectInput
          wrapperClassName={`generic-field generic-field-select ${classname}`}
          data-testid={name}
          noMargin
          name={name}
          value={getMultiSelectOptionFromValue(
            multiSelectOptions,
            value as string[] | undefined
          ) as SelectOption[]}
          disabled={disabled}
          onBlur={onBlur}
          labelTop={!!label}
          setDefaultSingleOption={setSingleOption}
          onChange={(val: (FieldSelectOption | undefined)[]) => {
            onChange(getMultiSelectValueForForm(val));
          }}
          onInputChange={(val: string) => onInputChange(val)}
          error={error as { message?: string; type?: string } | undefined}
          isClearable={clearable}
          placeholder={placeholder as string | undefined}
          isSearchable
          label={label as string | undefined}
          isLoading={loading}
          options={multiSelectOptions as SelectOption[] | undefined}
          isMulti
          required={required}
          hideLabelHelper={hideLabelHelper}
          description={description}
        />
      );
    case fieldTypeOptions.INLINE_AUTOCOMPLETE:
      return getTextEditor(true, false);

    case fieldTypeOptions.AUTOCOMPLETE_ONLY_TEXTAREA:
      return getTextEditor(false, false);

    case fieldTypeOptions.TEXT:
      return (
        <TextInput
          wrapperClassName={`generic-field generic-field-text ${classname}`}
          data-testid={name}
          noMargin
          name={name}
          value={value as string | number | undefined}
          disabled={disabled}
          onBlur={onBlur}
          labelTop
          onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
            onChange(val?.target?.value)
          }
          error={error as { message?: string; type?: string } | undefined}
          placeholder={placeholder as string | undefined}
          label={label as string | undefined}
          description={description as string | undefined}
          required={required}
          hideLabelHelper={hideLabelHelper}
          withHelp={help as string | undefined}
          max={max}
          viewMode={viewMode}
        />
      );
    case fieldTypeOptions.TEXT_AREA:
    case fieldTypeOptions.JSON:
      return (
        <Textarea
          wrapperClassName={`generic-field generic-field-text-area ${classname} ${name}-field`}
          data-testid={name}
          noMargin
          name={name}
          value={value as string | undefined}
          disabled={disabled}
          onBlur={onBlur}
          labelTop
          onChange={(data) => onChange(data)}
          error={error as TextareaErrorProps | undefined}
          label={label as string}
          required={required}
          hideLabelHelper={hideLabelHelper}
          withHelp={help as string | boolean}
          placeholder={placeholder as string}
        />
      );
    case fieldTypeOptions.HTML_TEXT_AREA:
      return getTextEditor(false, true);
    case fieldTypeOptions.TAGS_LIST:
      return (
        <TagsSelect
          wrapperClassName={`generic-field generic-field-tag-select ${classname}`}
          noMargin
          data-testid={name}
          // TagsSelect requires `name: string`; GenericField name is optional.
          name={name as string}
          viewMode={viewMode}
          maxSuggestionsLength={20}
          required={required}
          hideLabelHelper={hideLabelHelper}
          // ServerFieldOption.id is optional; TagsSelect's TagOption requires
          // `id: string | number`. Runtime tags always carry an id here.
          value={
            ((value as string[] | undefined)?.map((id) => {
              const item = options?.find((option) => option?.id === id);
              if (item) {
                return item;
              } else {
                return { id: id, name: id };
              }
            }) || []) as React.ComponentProps<typeof TagsSelect>["value"]
          }
          disabled={disabled}
          onBlur={onBlur}
          labelTop
          onChange={((tags: ServerFieldOption[]) =>
            onChange(tags?.map((tag) => tag.id))) as unknown as React.ComponentProps<
            typeof TagsSelect
          >["onChange"]}
          error={error as { message?: string; type?: string } | undefined}
          placeholder={placeholder as string | undefined}
          label={label as string | undefined}
          allowNew={creatable}
          suggestions={
            options as React.ComponentProps<typeof TagsSelect>["suggestions"]
          }
          minQueryLength={0}
          newTagTextPrefix={`+`}
          description={description}
        />
      );
    case fieldTypeOptions.MAPPING:
      return (
        <MappingField
          // `props` carries setValue/registerPrefix/formState as loose
          // pass-through types; MAPPING callers always supply the real RHF values.
          {...(props as React.ComponentProps<typeof MappingField>)}
          value={value as MappingFieldValue | undefined}
          columns={mapping?.columns as MappingFieldColumns | undefined}
          data={mapping?.rows as MappingFieldRowData[] | undefined}
        />
      );
    case fieldTypeOptions.UNIQUE_MAPPING:
      return (
        <MappingField
          {...(props as React.ComponentProps<typeof MappingField>)}
          value={value as MappingFieldValue | undefined}
          columns={mapping?.columns as MappingFieldColumns | undefined}
          data={mapping?.rows as MappingFieldRowData[] | undefined}
          isUnique
        />
      );
    case fieldTypeOptions.SELECT_QUERY:
      return (
        <SearchQueryField
          // `props` carries control/registerPrefix/params as loose pass-through
          // types on GenericFieldProps; SELECT_QUERY callers always supply the
          // real RHF values (same precedent as MappingField/DynamicMappingField).
          {...(props as React.ComponentProps<typeof SearchQueryField>)}
          viewMode={viewMode}
          input={fieldTypeOptions.SELECT}
          params={query?.params as string[] | undefined}
        />
      );
    case fieldTypeOptions.DYNAMIC_MAPPING:
      return (
        <DynamicMappingField
          // `props` carries control/formState/registerPrefix as loose
          // pass-through types on GenericFieldProps; at runtime DYNAMIC_MAPPING
          // callers always supply the real RHF values.
          {...(props as React.ComponentProps<typeof DynamicMappingField>)}
          options={mapping?.rows?.map((row) => row?.field_from)}
        />
      );
    case fieldTypeOptions.NUMBER:
      return (
        <TextInput
          wrapperClassName={`generic-field generic-field-text-number ${classname}`}
          noMargin
          data-testid={name}
          name={name}
          value={(value as { toString?: () => string } | undefined)?.toString?.()}
          disabled={disabled}
          type={"number"}
          onBlur={onBlur}
          labelTop
          onChange={(val: unknown) => onChange(val)}
          error={error as { message?: string; type?: string } | undefined}
          placeholder={placeholder as string | undefined}
          label={label as string | undefined}
          description={description as string | undefined}
          required={required}
          hideLabelHelper={hideLabelHelper}
          withHelp={help as string | undefined}
        />
      );
    case fieldTypeOptions.DATE:
      return (
        <DateInput
          onBlur={onBlur}
          name={name}
          labelTop
          noMargin
          required={required}
          // DateInput's onChange receives a DateInputChangeEvent; the original
          // JS reads `e.target.value`, which both event shapes expose.
          onChange={
            ((e: React.ChangeEvent<HTMLInputElement>) => {
              onChange(parseDateInputToIsoDateString(e?.target?.value));
            }) as unknown as React.ComponentProps<typeof DateInput>["onChange"]
          }
          label={label as string | undefined}
          error={error as string | null | undefined}
          value={value as string | null | undefined}
          hideLabelHelper={hideLabelHelper}
        />
      );
    case fieldTypeOptions.RADIO:
      const radioOptions = formatRadioOptionsFromServer(options);

      return (
        <RadioButtonsList
          wrapperClassName={`generic-field generic-field-radio-button ${classname}`}
          data-testid={name}
          noMargin
          name={name}
          value={value}
          disabled={disabled}
          onBlur={onBlur}
          labelTop
          onChange={(val: unknown) => onChange(val)}
          setFirstAsDefault={setFirstAsDefault}
          error={error}
          label={label as string | undefined}
          required={required}
          options={radioOptions}
          // GenericField types `direction` as a free string; RadioButtonsList
          // narrows to "row" | "column". Runtime values are one of these.
          direction={direction as "row" | "column" | undefined}
          alignLeft
        />
      );
    case fieldTypeOptions.COLOR_SELECT:
      const colorSelectOptions = formatColorSelectOptionsFromServer(options);
      return (
        <SelectInput
          wrapperClassName={`generic-field generic-field-select ${classname}`}
          data-testid={name}
          noMargin
          inputValue={inputValue}
          name={name}
          value={
            getSelectOptionFromValue(colorSelectOptions, value) as SelectOption | null
          }
          disabled={disabled}
          onBlur={onBlur}
          labelTop={!!label}
          setDefaultSingleOption={setSingleOption}
          onChange={(val: FieldSelectOption | null) =>
            onChange(getSelectValueForForm(val))
          }
          onInputChange={(val: string) => onInputChange(val)}
          error={error as { message?: string; type?: string } | undefined}
          isClearable={clearable}
          placeholder={placeholder as string | undefined}
          isSearchable
          label={label as string | undefined}
          isLoading={loading}
          options={colorSelectOptions as SelectOption[] | undefined}
          required={required}
          hideLabelHelper
        />
      );
    case fieldTypeOptions.CHECKBOX:
    case fieldTypeOptions.BOOLEAN:
      return (
        <CheckBox
          name={name}
          data-testid={name}
          // `value` is `unknown`; CheckBox narrows to boolean.
          checked={value as boolean | undefined}
          className={`generic-field generic-field-checkbox ${classname}`}
          label={label}
          onChange={(val: unknown, newValue: unknown) => {
            onChange(newValue);
          }}
          // `error` is `unknown`; CheckBox narrows to ErrorProps.
          error={error as { message: string; type: string } | undefined}
          noMargin
          description={description as string | undefined}
        />
      );
    default:
      return <></>;
  }
};

export default GenericField;
